import React, { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import { highlightPptx, scrollToSlide } from "./pptxHighlighter";
import { triggerHighlight } from "../DocxViewer/docxHighlighter";
import { removeNodeHighlights } from "../utils/addOnServices/highlightTextNodes";
import { ErrorBoundary } from "react-error-boundary";
import {
  clearProductTimeout,
  setProductTimeout,
  type ProductTimer,
} from "@/utils/productTimers";

interface ResponseLike {
  json: () => Promise<any>;
  blob: () => Promise<Blob>;
}

interface PptxViewerProps {
  fileUrl?: string;
  fileContentResponse?: ResponseLike;
}

// Extend the global Window interface to include jQuery
declare global {
  interface Window {
    $: any;
    jQuery: any;
    __pptxDefinePropertyPatched?: boolean;
  }
}

// Global patch for Object.defineProperty to handle conflicts with React
// This must be applied at module load time, before React renders
if (typeof window !== "undefined" && !window.__pptxDefinePropertyPatched) {
  const originalDefineProperty = Object.defineProperty;

  Object.defineProperty = function (
    obj: any,
    prop: string,
    descriptor: PropertyDescriptor
  ) {
    try {
      return originalDefineProperty.call(this, obj, prop, descriptor);
    } catch (error: any) {
      // Handle property descriptor conflicts gracefully
      if (
        error?.message?.includes("property descriptor") ||
        error?.message?.includes("Cannot both specify accessors") ||
        error?.message?.includes("Invalid property descriptor")
      ) {
        // Get existing descriptor
        const existingDescriptor = Object.getOwnPropertyDescriptor(obj, prop);

        // If new descriptor has both accessors and value, prefer accessors
        if (descriptor.get || descriptor.set) {
          const { value, writable, ...accessorDescriptor } = descriptor;
          try {
            return originalDefineProperty.call(
              this,
              obj,
              prop,
              accessorDescriptor
            );
          } catch {
            // If that fails, check if we can use the existing descriptor
            if (existingDescriptor) {
              // Keep existing descriptor, skip the update
              return obj;
            }
            // Skip the property definition entirely
            return obj;
          }
        }

        // If existing has accessors but new has value, skip the update
        if (
          existingDescriptor &&
          (existingDescriptor.get || existingDescriptor.set)
        ) {
          return obj;
        }

        // If existing has value but new has accessors, try to remove value first
        if (
          existingDescriptor &&
          (existingDescriptor.value !== undefined ||
            existingDescriptor.writable !== undefined)
        ) {
          try {
            // Try to delete the property first (if configurable)
            if (existingDescriptor.configurable) {
              delete (obj as any)[prop];
              return originalDefineProperty.call(this, obj, prop, descriptor);
            }
          } catch {
            // If deletion fails, skip
            return obj;
          }
        }

        // Last resort: skip the property definition
        return obj;
      }

      // Re-throw non-descriptor errors
      throw error;
    }
  } as typeof Object.defineProperty;

  window.__pptxDefinePropertyPatched = true;
}

const PptxViewer: React.FC<PptxViewerProps> = ({
  fileUrl,
  fileContentResponse,
}) => {
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [scriptsLoaded, setScriptsLoaded] = useState(false);
  const [loadingStatus, setLoadingStatus] = useState<string>("Initializing...");
  const containerRef = useRef<HTMLDivElement>(null);
  const fileViewerData = useSelector(
    (state: RootState) => state.fileViewerData
  );

  // Load external scripts needed for PPTXJS
  useEffect(() => {
    setLoadingStatus("Loading required libraries...");

    const loadScript = (src: string): Promise<void> => {
      return new Promise((resolve, reject) => {
        if (document.querySelector(`script[src="${src}"]`)) {
          resolve();
          return;
        }

        const script = document.createElement("script");
        script.src = src;
        script.onload = () => {
          resolve();
        };
        script.onerror = () => {
          console.error(`PptxViewer: Failed to load script: ${src}`);
          reject(new Error(`Failed to load script: ${src}`));
        };
        document.head.appendChild(script);
      });
    };

    const loadStylesheet = (href: string): Promise<void> => {
      return new Promise((resolve, reject) => {
        if (document.querySelector(`link[href="${href}"]`)) {
          resolve();
          return;
        }

        const link = document.createElement("link");
        link.rel = "stylesheet";
        link.href = href;
        link.onload = () => {
          resolve();
        };
        link.onerror = () => {
          console.error(`PptxViewer: Failed to load stylesheet: ${href}`);
          reject(new Error(`Failed to load stylesheet: ${href}`));
        };
        document.head.appendChild(link);
      });
    };

    const loadPptxJs = async () => {
      try {
        // Load required stylesheets
        await loadStylesheet("/pptxjs/pptxjs.css");
        await loadStylesheet("/pptxjs/nv.d3.min.css");

        // Load required scripts in order
        setLoadingStatus("Loading jQuery...");
        await loadScript("https://code.jquery.com/jquery-3.7.1.min.js");

        setLoadingStatus("Loading JSZip...");
        await loadScript("/pptxjs/jszip.min.js");

        setLoadingStatus("Loading FileReader...");
        await loadScript("/pptxjs/filereader.js");

        setLoadingStatus("Loading D3.js...");
        await loadScript("/pptxjs/d3.v3.min.js");

        setLoadingStatus("Loading NV.D3...");
        await loadScript("/pptxjs/nv.d3.min.js");

        setLoadingStatus("Loading PPTXJS...");
        await loadScript("/pptxjs/pptxjs.js");

        // Verify jQuery is available
        if (typeof window.$ === "undefined") {
          throw new Error("jQuery not available after loading");
        }

        // Verify D3 is available
        if (typeof (window as any).d3 === "undefined") {
          console.warn("PptxViewer: D3 not available - charts may not work");
        }

        // Verify NV is available
        if (typeof (window as any).nv === "undefined") {
          console.warn("PptxViewer: NV.D3 not available - charts may not work");
        }

        // Verify pptxToHtml method is available
        if (typeof window.$.fn.pptxToHtml === "undefined") {
          console.warn("PptxViewer: pptxToHtml method not found on jQuery");
        }

        setScriptsLoaded(true);
      } catch (err) {
        console.error("PptxViewer: Failed to load PPTXJS dependencies:", err);
        setError("Failed to load presentation converter");
        setIsLoading(false);
      }
    };

    loadPptxJs();
  }, []);

  // Convert PPTX to HTML using PPTXJS
  useEffect(() => {
    if (!scriptsLoaded) {
      return;
    }

    // Stops the conversion monitor (observer + deadline) this run started,
    // once it has started; the effect cleanup calls it.
    let stopMonitoring: (() => void) | null = null;

    const convertPptx = async () => {
      try {
        setIsLoading(true);
        setError(null);
        setLoadingStatus("Preparing file for conversion...");

        // Bytes the caller already loaded win over the URL. Both are passed
        // together, and the fetch below is a bare one — it carries no
        // Authorization header, so re-fetching an authenticated file URL fails
        // where the caller's own fetch succeeded (#10988).
        let pptxBlob: Blob;
        if (fileContentResponse) {
          setLoadingStatus("Processing file content...");
          pptxBlob = await fileContentResponse.blob();
        } else if (fileUrl) {
          setLoadingStatus("Downloading presentation...");
          const response = await fetch(fileUrl);

          if (!response.ok) {
            throw new Error(
              `Failed to fetch file: ${response.status} ${response.statusText}`
            );
          }

          pptxBlob = await response.blob();
        } else {
          throw new Error("No file URL or content provided");
        }

        // Validate blob
        if (pptxBlob.size === 0) {
          throw new Error("File is empty");
        }

        // Clear the container
        if (containerRef.current) {
          containerRef.current.innerHTML = "";
        }

        // Create a blob URL for PPTXJS to load
        setLoadingStatus("Preparing for conversion...");
        const blobUrl = URL.createObjectURL(pptxBlob);

        // Use PPTXJS to convert the file
        if (window.$ && containerRef.current) {
          // Defer initialization to next frame to avoid React property descriptor conflicts
          requestAnimationFrame(() => {
            try {
              // Ensure we have a fresh reference to the container
              if (!containerRef.current) {
                throw new Error("Container element no longer available");
              }

              const $container = window.$(containerRef.current);

              setLoadingStatus("Converting presentation to HTML...");

              // Check if pptxToHtml is available
              if (typeof $container.pptxToHtml !== "function") {
                throw new Error(
                  "pptxToHtml method not available on jQuery object"
                );
              }

              // Configure and run PPTXJS using the blob URL
              // The global Object.defineProperty patch will handle any conflicts
              $container.pptxToHtml({
                pptxFileUrl: blobUrl,
                slideMode: false,
                keyBoardShortCut: false,
                mediaProcess: true,
                themeProcess: true,
                slidesScale: "fit",
                jsZipV2: "/pptxjs/jszip.min.js",
              });

              // Set up monitoring for conversion completion
              setupConversionMonitoring(blobUrl);
            } catch (initError) {
              console.error("PptxViewer: Initialization error:", initError);
              setError(
                initError instanceof Error
                  ? initError.message
                  : "Failed to initialize presentation viewer"
              );
              setIsLoading(false);
              URL.revokeObjectURL(blobUrl);
            }
          });

          // Set up monitoring for conversion completion
          const setupConversionMonitoring = (blobUrl: string) => {
            let deadline: ProductTimer | null = null;
            let observer: MutationObserver | null = null;

            const stopObserving = () => {
              observer?.disconnect();
              observer = null;
            };

            const checkConversionComplete = () => {
              if (!containerRef.current) {
                return;
              }

              // Check if slides have been generated
              const slides = containerRef.current.querySelectorAll(
                '.slide, [id^="slide"]'
              );

              if (slides.length > 0) {
                stopObserving();
                clearProductTimeout(deadline);

                setIsLoading(false);
                URL.revokeObjectURL(blobUrl);

                // Add slide classes for highlighting support and apply responsive scaling
                slides.forEach((slide, index) => {
                  slide.classList.add(`pptx-slide-${index + 1}`);
                });

                // Apply responsive scaling and positioning
                const applySlideScaling = () => {
                  slides.forEach((slide, index) => {
                    const slideElement = slide as HTMLElement;

                    // Reset transforms to get original dimensions
                    slideElement.style.transform = "";
                    const slideWidth = slideElement.offsetWidth || 1280;

                    if (!containerRef.current) return;

                    const containerPadding = 40;
                    const containerWidth = containerRef.current.offsetWidth;
                    const availableWidth = containerWidth - containerPadding;

                    if (availableWidth > 0 && slideWidth > 0) {
                      // Calculate scale - ONLY SHRINK, never grow
                      const optimalScale = availableWidth / slideWidth;
                      const scale = Math.min(optimalScale, 1.0); // Cap at 1.0 - never grow

                      // Only apply scaling if we need to shrink
                      if (scale < 1.0) {
                        // Calculate horizontal centering
                        const scaledWidth = slideWidth * scale;
                        const translateX = Math.max(
                          0,
                          (availableWidth - scaledWidth) / 2
                        );

                        // Apply only scaling and horizontal centering
                        slideElement.style.transformOrigin = "top left";
                        slideElement.style.transform = `scale(${scale}) translateX(${translateX}px)`;

                        // transform: scale() is visual-only — the slide still
                        // occupies its full unscaled height in layout, which
                        // reads as a huge gap before the next slide at pane
                        // widths. Collapse the leftover with a negative
                        // bottom margin ("important" because the stylesheet
                        // above zeroes slide margins with !important).
                        const slideHeight = slideElement.offsetHeight;
                        if (slideHeight > 0) {
                          const layoutOverhang = Math.round(
                            slideHeight * (1 - scale)
                          );
                          // Keep a small real gap between slides.
                          slideElement.style.setProperty(
                            "margin-bottom",
                            `${12 - layoutOverhang}px`,
                            "important"
                          );
                        }

                        console.log(
                          `Slide ${index + 1}: scale=${scale.toFixed(3)}, translateX=${translateX.toFixed(1)}`
                        );
                      } else {
                        slideElement.style.removeProperty("margin-bottom");
                        // No scaling needed, just center if there's extra space
                        const translateX = Math.max(
                          0,
                          (availableWidth - slideWidth) / 2
                        );
                        if (translateX > 0) {
                          slideElement.style.transformOrigin = "top left";
                          slideElement.style.transform = `translateX(${translateX}px)`;
                          console.log(
                            `Slide ${index + 1}: no scaling, translateX=${translateX.toFixed(1)}`
                          );
                        } else {
                          // No transform needed
                          slideElement.style.transform = "";
                          console.log(
                            `Slide ${index + 1}: no scaling or centering needed`
                          );
                        }
                      }
                    }
                  });
                };

                // The slide markup was just written outside React (by
                // pptxjs, via jQuery); a layout read of it needs to wait
                // for the browser to paint that write.
                requestAnimationFrame(() => {
                  applySlideScaling();
                });

                // Add window resize handler to recalculate on window size changes
                const handleResize = () => {
                  applySlideScaling();
                };

                window.addEventListener("resize", handleResize);

                // Store cleanup function for later use
                (containerRef.current as any)._cleanupResize = () => {
                  window.removeEventListener("resize", handleResize);
                };

                return;
              }

              // Check for error messages
              const errorElements = containerRef.current.querySelectorAll(
                '.error, .alert, [class*="error"]'
              );
              if (errorElements.length > 0) {
                errorElements.forEach((el) =>
                  console.log("Error element:", el.textContent)
                );
                stopObserving();
                clearProductTimeout(deadline);
                setError(
                  "Conversion failed - error in presentation processing"
                );
                setIsLoading(false);
                URL.revokeObjectURL(blobUrl);
                return;
              }
            };

            // Watch for pptxjs injecting the slide markup (or an error
            // element) — it writes outside React, via jQuery — instead of
            // polling for it.
            if (containerRef.current) {
              observer = new MutationObserver(checkConversionComplete);
              observer.observe(containerRef.current, {
                childList: true,
                subtree: true,
              });
            }
            stopMonitoring = () => {
              stopObserving();
              clearProductTimeout(deadline);
            };

            // Set up overall timeout (extended for larger files)
            deadline = setProductTimeout(() => {
              stopObserving();

              const slides = containerRef.current?.querySelectorAll(
                '.slide, [id^="slide"]'
              );
              if (slides && slides.length > 0) {
                setIsLoading(false);
                slides.forEach((slide, index) => {
                  slide.classList.add(`pptx-slide-${index + 1}`);
                });
              } else {
                setError(
                  "Conversion timeout - the presentation may be too large or complex"
                );
                setIsLoading(false);
              }

              URL.revokeObjectURL(blobUrl);
            }, 30000); // Extended to 30 seconds

            // The DOM may already carry slides (or an error) by the time
            // the observer above attaches. Checked after the deadline is
            // armed, so a hit here clears it.
            checkConversionComplete();
          };
        } else {
          const missingItems = [];
          if (!window.$) missingItems.push("jQuery");
          if (!containerRef.current) missingItems.push("container");

          throw new Error(`Missing required items: ${missingItems.join(", ")}`);
        }
      } catch (err) {
        console.error("PptxViewer: Error converting PPTX file:", err);
        setError(
          err instanceof Error ? err.message : "Failed to convert PPTX file"
        );
        setIsLoading(false);
      }
    };

    // The container div renders unconditionally on mount, so by the time
    // this effect runs (after commit) the ref is already attached.
    if (containerRef.current) {
      convertPptx();
    }
    return () => {
      stopMonitoring?.();
    };
  }, [fileUrl, fileContentResponse, scriptsLoaded]);

  // Cleanup resize handler on unmount
  useEffect(() => {
    return () => {
      if (
        containerRef.current &&
        (containerRef.current as any)._cleanupResize
      ) {
        (containerRef.current as any)._cleanupResize();
      }
    };
  }, []);

  // Handle highlighting and scrolling similar to other viewers
  useEffect(() => {
    if (!containerRef.current || isLoading) return;

    // Handle scrolling to specific slide
    if (fileViewerData.highlightPageNum) {
      const success = scrollToSlide(
        containerRef.current,
        fileViewerData.highlightPageNum
      );

      if (!success) {
        const pageElement = document.querySelector(
          `.pptx-slide-${fileViewerData.highlightPageNum}`
        ) as HTMLElement;
        if (pageElement) {
          pageElement.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }
    }

    // Handle text highlighting
    const highlightContext = (fileViewerData as any).highlightContext;
    const originalNumValue = (fileViewerData as any).originalNumValue;

    if (highlightContext) {
      highlightPptx(
        fileViewerData.highlightPageNum || 1,
        highlightContext,
        String(originalNumValue || ""),
        fileViewerData.exactOnly
      );
    }
  }, [isLoading, fileViewerData]);

  if (error) {
    return (
      <div className="w-full h-full p-4 px-12 pptx-viewer overflow-auto relative">
        <div
          ref={containerRef}
          className="pptx-container w-full"
          style={{ minHeight: "100%" }}
        />
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-white bg-opacity-90">
          <div className="text-red-500 mb-2">Error loading presentation</div>
          <div className="text-sm text-gray-600">{error}</div>
          <div className="text-xs text-gray-500 mt-2">
            Please try downloading the file instead
          </div>
        </div>
      </div>
    );
  }

  return (
    <ErrorBoundary
      fallback={
        <div className="w-full h-full p-4 px-12 pptx-viewer overflow-auto relative">
          <div className="flex flex-col items-center justify-center h-full bg-white bg-opacity-90">
            <div className="text-red-500 mb-2 text-lg font-bold">
              Error loading presentation
            </div>
            <div className="text-sm text-gray-600 mb-4">
              The presentation could not be loaded due to a rendering error.
            </div>
            <div className="text-xs text-gray-500">
              Please try downloading the file instead or refresh the page.
            </div>
          </div>
        </div>
      }
      onError={(error, errorInfo) => {
        console.error(
          "PptxViewer ErrorBoundary caught error:",
          error,
          errorInfo
        );
        setError(error.message || "Failed to render presentation");
      }}
    >
      <div
        className="w-full h-full p-4 px-12 pptx-viewer overflow-auto relative"
        onClick={(e) => {
          // Check if the clicked element is a link or contained within a link
          const target = e.target as HTMLElement;
          const link = target.closest("a");

          if (link && link.href) {
            e.preventDefault();
            e.stopPropagation();
            window.open(link.href, "_blank", "noopener,noreferrer");
            return;
          }

          removeNodeHighlights(".pptx-viewer");
        }}
      >
        <style>{`
        /* PPTX-specific highlight styles */
        .pptx-highlight {
          background-color: rgba(255, 255, 0, 0.4) !important;
          border-radius: 2px;
          box-shadow: 0 0 0 1px rgba(255, 193, 7, 0.5);
          z-index: 10;
          position: relative;
        }
        
        /* Ensure highlights are visible on scaled slides */
        .pptx-viewer .fp-dark-highlight {
          background-color: rgba(255, 255, 0, 0.6) !important;
          color: inherit !important;
          padding: 1px 2px;
          border-radius: 2px;
          box-shadow: 0 0 0 1px rgba(255, 193, 7, 0.7);
          position: relative;
          z-index: 10;
        }
        
        /* Make sure highlights work with CSS transforms */
        .pptx-viewer [class*="pptx-slide-"] .fp-dark-highlight,
        .pptx-viewer [class*="pptx-slide-"] .pptx-highlight {
          transform: none !important; /* Override any slide-level transforms */
          position: relative;
          z-index: 15;
        }
        
        /* Smooth scrolling for highlighted elements */
        .pptx-viewer {
          scroll-behavior: smooth;
        }

        /* Container and slide positioning overrides */
        .pptx-viewer .pptx-container {
          display: flex !important; /* Restore flexbox for proper scaling */
          flex-direction: column !important;
          align-items: flex-start !important; /* Restore for slide positioning */
          padding: 20px !important; /* Ensure consistent padding */
          box-sizing: border-box !important;
          width: 100% !important;
          overflow-x: visible !important; /* Restore original overflow setting */
        }
        
        /* Slide positioning and spacing */
        .pptx-viewer [id^="slide"], 
        .pptx-viewer .slide {
          margin-top: 0 !important; /* Clear top margin for our JavaScript margin-top */
          margin-left: 0 !important;
          margin-right: 0 !important;
          margin-bottom: 0 !important; /* Clear bottom margin for translateY */
          width: auto !important;
          max-width: none !important;
          position: relative !important;
          display: block !important;
          transform-origin: top left !important; /* Ensure consistent transform origin */
          transition: transform 0.2s ease !important; /* Restore smooth transitions */
        }
        
        /* Prevent any flexbox interference */
        .pptx-viewer [id^="slide"] > *,
        .pptx-viewer .slide > * {
          position: relative !important;
        }
      `}</style>
        <div
          ref={containerRef}
          className="pptx-container w-full"
          style={{ minHeight: "100%" }}
        />
        {isLoading && (
          <div className="absolute inset-0 flex justify-center items-center bg-white bg-opacity-90">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-2"></div>
              <div>{loadingStatus}</div>
              <div className="text-xs text-gray-500 mt-2">
                This may take a moment for larger presentations
              </div>
            </div>
          </div>
        )}
      </div>
    </ErrorBoundary>
  );
};

export default PptxViewer;
